import React from "react";

import type { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import styles from "./LoadingSpinner.module.css";

interface Props {
	/** Only show the spinner after this many milliseconds */
	delay?: number;
	/** If true, renders inline instead of absolutely centring; if "centered", renders inline, but center-aligned  */
	inline?: boolean | "centered";
	/** Render the loader inside an element that dims the nearest positioned ancestor */
	dimmer?: boolean;
	size?: FontAwesomeIconProps["size"];
	className?: string;
}

const SIZE_TO_PX: Record<string, number> = {
	"2xs": 10,
	xs: 12,
	sm: 14,
	lg: 24,
	xl: 28,
	"2xl": 32,
	"1x": 16,
	"2x": 32,
	"3x": 48,
	"4x": 64,
	"5x": 80,
	"6x": 96,
	"7x": 112,
	"8x": 128,
	"9x": 144,
	"10x": 160,
};

function getSpinnerSize(size: FontAwesomeIconProps["size"]): number {
	if (!size) return SIZE_TO_PX.lg;
	return SIZE_TO_PX[String(size)] ?? SIZE_TO_PX.lg;
}

export default function LoadingSpinner({ delay, inline, size = "lg", dimmer = true, className }: Props) {
	const [show, setShow] = React.useState(!delay);
	const spinnerSize = getSpinnerSize(size);

	React.useEffect(() => {
		if (!delay) return;
		const timeout = window.setTimeout(() => setShow(true), delay);
		return () => window.clearTimeout(timeout);
	}, [delay, setShow]);

	const strokeWidth = Math.max(3, Math.sqrt(spinnerSize) - 2);
	const loaderNode = (
		<span
			aria-label="Loading…"
			role="status"
			className={cx(className, styles.spinner, {
				[styles.fadeable]: !dimmer,
				[styles.hidden]: !dimmer && !show,
				[styles.inline]: Boolean(inline),
				[styles.centered]: inline === "centered",
			})}
		>
			<svg className={styles.svg} viewBox="0 0 50 50" width={spinnerSize} height={spinnerSize} aria-hidden>
				<circle className={styles.path} cx="25" cy="25" r="20" fill="none" strokeWidth={strokeWidth} />
			</svg>
		</span>
	);

	if (dimmer) return <div className={cx(styles.dimmer, styles.fadeable, { [styles.hidden]: !show })}>{loaderNode}</div>;
	return loaderNode;
}
